import i18n from "i18n";
import { errorCode } from "features/positions/positions-errors";

type TFunction = (key: string, options?: Record<string, unknown>) => string;

/** The longest amendment number the backend accepts (it answers AMENDMENT_NUMBER_TOO_LONG beyond it). */
export const AMENDMENT_NUMBER_MAX_LENGTH = 100;

/** Backend refusals of the amendment requests (register, edit registered, set-terms, create, edit), and the text for each. */
const ERROR_KEYS: Record<string, string> = {
  AMENDMENT_NUMBER_REQUIRED: "amendment_number_required",
  AMENDMENT_NUMBER_TOO_LONG: "amendment_number_too_long",
  EFFECTIVE_DATE_REQUIRED: "amendment_effective_date_required",
  FILE_REQUIRED: "amendment_scan_required",
  FILE_CHOICE_CONFLICT: "amendment_file_choice_conflict",
  FILE_IN_SIGNING: "amendment_file_in_signing",
  FILE_ALREADY_USED: "amendment_file_already_used",
  AMENDMENT_NUMBER_TAKEN: "amendment_number_taken",
  POSITIONS_REQUIRED: "positions_required",
  FILE_NOT_FOUND: "amendment_scan_not_found",
  FILE_NOT_A_FRESH_UPLOAD: "amendment_scan_not_fresh",
  ONBOARDING_NOT_FOUND: "amendment_onboarding_not_found",
  AMENDMENT_NOT_SIGNED: "amendment_not_signed",
  AMENDMENT_NOT_REGISTERED: "amendment_not_registered",
  AMENDMENT_NOT_FOUND: "amendment_not_found",
  AMENDMENT_DOCUMENT_ANNULLED: "amendment_document_annulled",
  TERMS_ALREADY_SET: "amendment_terms_already_set",
};

/** Someone else gave the amendment its terms first (409 from set-terms). */
export const isTermsAlreadySet = (error: any): boolean => errorCode(error) === "TERMS_ALREADY_SET";

/** The scan that was just uploaded is gone or already used elsewhere, so it must be uploaded again. */
export const isUploadedScanRefused = (error: any): boolean =>
  errorCode(error) === "FILE_NOT_FOUND" || errorCode(error) === "FILE_NOT_A_FRESH_UPLOAD";

/** The i18n key for the response's code, or null when it has none of its own. */
function knownMessageKey(error: any): string | null {
  const code = errorCode(error);
  return code !== null && Object.prototype.hasOwnProperty.call(ERROR_KEYS, code) ? ERROR_KEYS[code] : null;
}

/** True when the response carries a code that has its own translated text. */
export const hasKnownAmendmentCode = (error: any): boolean => knownMessageKey(error) !== null;

/**
 * Translated text for a failed request: a known code, else the server's
 * message (translated when the server sent an i18n key such as
 * `document_withdrawn`), else the generic error.
 */
export function describeAmendmentError(error: any, t: TFunction): string {
  const key = knownMessageKey(error);
  if (key) return t(key);
  const message = error?.response?.data?.message;
  if (typeof message !== "string" || !message) return t("error");
  // `as any`: the "i18n" import is typed loosely here (same cast as utils/invoice-errors.ts).
  return (i18n as any).exists(message) ? t(message) : message;
}
